import { useEffect, useRef, useState } from 'react';
import { C } from '../../../lib/theme';
import { styles } from '../../../lib/styles';
import type { Case } from '../../../domain/initial-cases';
import { useAppData } from '../../../state/contexts/AppDataContext';
import { SectionEvidenceAttachments } from '../../../components/EvidenceAttachments';

// WHAT THE ANALYST CAN HONESTLY ATTEST TO, AND WHERE THE REST GOES (#697).
//
// WHY THE ATTESTATION IS NOT GATED ON A PATH. The three cards — registered,
// common law, licensed — are the three ways a claim can be SUPPORTED, not a
// partition of the registrants who exist. One who has never registered the mark
// and holds no licence fits none of them. Gating the checkbox on a card would
// therefore make the attestation unreachable for exactly that registrant, and
// that is the file most likely to be disputed later: an unregistered mark is
// what a third-party claimant turns up to contest. So the analyst's search has
// to be recordable whether or not a card fits, and path selection stays a
// refinement of the attestation rather than a precondition for it.
//
// WHY THE WORDING IS BEST-EFFORT. A search establishes what was looked for and
// what came back. It does not establish a chain of title, and it cannot be
// stretched into one. In particular an attestation phrased around written
// authorization holds on the LICENSED path only — on the registered and
// common-law paths there is no such document to hold, so that phrasing would
// have the analyst signing a sentence untrue of two thirds of the cases they
// see. Wording the box to the evidence a search actually yields is what keeps
// the tick worth something in a dispute; a stronger sentence nobody can honestly
// mean is weaker evidence than a modest one everybody can.
//
// WHERE THE SPECIFICS LIVE. The note, and it is the reason the checkbox can
// afford to be modest. "Searched TESS and the Copyright Office, nothing on
// file" is a finding, not a blank, and it is the finding the best-effort wording
// leaves room for. The audit detail records `path: null` when no card was
// chosen, so the trail distinguishes "no path claimed" from "no path recorded".

// Shared by the checkbox's `id` and the textarea's blur guard, so the two cannot
// drift apart into a guard that silently never matches — a guard that never
// matches restores the double PATCH without any visible symptom.
const ATTESTATION_ID = 'ip-attestation';
const ATTESTATION_LABEL =
  'I searched for this IP (trademark/copyright registries, public use, or licence ' +
  'documents as applicable) and, to the best of my ability, confirmed the registrant ' +
  'owns it or has the right to use it.';

// #766 — the IP attestation, exported so the case progress tracker lists it as
// open on exactly the condition this tab's checkbox shows unticked.
export function ipVerified(c: Pick<Case, 'ip'>): boolean {
  return !!c.ip?.verified;
}

export function IpTab({ c }: { c: Case }) {
  const { updateCase } = useAppData();
  const storedNote: string = c.ip.searchNote ?? '';
  // Local draft so the textarea does not PATCH on every keystroke; it commits on
  // blur.
  //
  // Deliberately not `components/DraftTextInput` (#688), which solves the same
  // per-keystroke-PATCH problem for the Overview tab. That one renders an
  // `<input>` and commits on Enter — right for a one-line field, wrong for a
  // note whose whole job is a few lines of prose about what was searched, where
  // Enter has to insert a newline. Reusing it would mean adding a multiline mode
  // to its contract, which is that component's call to make and not this tab's.
  // Its STATE DISCIPLINE is borrowed wholesale, though, because the two hazards
  // it names apply here unchanged:
  //
  //   `noteFocused` — never replace a draft somebody is typing into. The resync
  //   below is keyed on the stored value so it fires when the server's copy
  //   really moves (the 409-refetch path), and on the case id so switching cases
  //   in a live tab cannot carry one registrant's findings into another's file.
  //
  //   `agreedNote` — the value this field and the case last agreed on. It moves
  //   on a resync AND on a write, which is what makes "did the analyst change
  //   this" answerable without comparing against a `storedNote` that may itself
  //   have moved underneath. Without it, focusing the note, typing nothing, and
  //   tabbing away after the stored value changed would write the pre-change
  //   text back over the newer one.
  const [draftNote, setDraftNote] = useState(storedNote);
  const noteFocused = useRef(false);
  const agreedNote = useRef(storedNote);

  useEffect(() => {
    if (noteFocused.current) return;
    agreedNote.current = storedNote;
    setDraftNote(storedNote);
  }, [c.id, storedNote]);

  const commitNote = (next: string) => {
    if (next === agreedNote.current) return;
    agreedNote.current = next;
    updateCase(c.id, { ip: { ...c.ip, searchNote: next } });
  };

  return (
    <div style={styles.card}>
      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Intellectual Property Due Diligence</h3>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>Verify IP rights for Call Logo, Caller Display Name, and Call Reason content</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        {[
          { path: 'registered',  label: 'Registered IP',     desc: 'USPTO TESS / Copyright Office — registration is presumptive evidence of IP rights', link: 'https://tmsearch.uspto.gov/search/search-information', linkLabel: 'USPTO TESS ↗' },
          { path: 'common_law',  label: 'Common Law Rights', desc: "Public domain evidence of prior use in commerce associated with registrant's legal entity", link: undefined, linkLabel: undefined },
          { path: 'licensed',    label: 'Licensed IP',       desc: 'LOA or Brand Authorization Form from IP owner confirming license to registrant', link: undefined, linkLabel: undefined },
        ].map(p => (
          <div key={p.path} style={{ background: c.ip.path === p.path ? C.goldBg : C.bgInput, border: `1px solid ${c.ip.path === p.path ? C.goldText : C.border}`, borderRadius: 8, padding: 12, cursor: 'pointer' }} onClick={() => updateCase(c.id, { ip: { ...c.ip, path: p.path } })}>
            <div style={{ fontSize: 13, fontWeight: 600, color: c.ip.path === p.path ? C.goldText : C.text }}>{p.label}</div>
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>{p.desc}</div>
            {p.link && <a href={p.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: C.goldText, display: 'block', marginTop: 4 }}>{p.linkLabel}</a>}
          </div>
        ))}
      </div>
      {/* `alignItems: 'flex-start'`, overriding `checkItem`'s centring: this
          attestation is a full sentence and wraps to two or three lines at the
          card's width, and a box centred against a three-line block reads as
          belonging to the middle line. Overridden here and not in
          `styles.checkItem`, which the one-line tickboxes on the other tabs
          still want centred. */}
      <div style={{ ...styles.checkItem, alignItems: 'flex-start' }}>
        {/* One PATCH per logical action, and the note rides along with the tick:
            a note typed and then attested without an intervening blur is part of
            the same act, so dropping it here would lose the evidence for the
            attestation being sent in the very same request. */}
        <input
          id={ATTESTATION_ID}
          type="checkbox"
          checked={ipVerified(c)}
          onChange={e => {
            // This write IS the note's commit as well, so the field and the case
            // now agree on the draft. Recording that is what makes the blur this
            // click already caused a no-op instead of a second PATCH.
            agreedNote.current = draftNote;
            updateCase(c.id, {
              ip: { ...c.ip, verified: e.target.checked, searchNote: draftNote },
              action: {
                code: 'IP_VERIFIED',
                // `?? null`, not a bare `c.ip.path`: with no card selected that is
                // `undefined`, which JSON.stringify DROPS. The audit row would be
                // missing the key altogether instead of recording that no path was
                // claimed, and a reader cannot tell those two apart afterwards.
                detail: { path: c.ip.path ?? null, verified: e.target.checked, searchNote: draftNote },
              },
            });
          }}
          // Catches the analyst who typed a note and then SHIFT-TABBED to the
          // checkbox without ticking it. The textarea deferred to the tick that
          // never came, so the note would otherwise be dropped in silence. A
          // no-op whenever the tick did happen, because the handler above has
          // already moved `agreedNote` to this same draft.
          onBlur={() => commitNote(draftNote)}
        />
        <label htmlFor={ATTESTATION_ID}>{ATTESTATION_LABEL}</label>
      </div>
      <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4, marginBottom: 8 }}>
        If the registrant has not registered the IP, record what you searched and what you found in the note.
      </div>
      <label htmlFor="ip-search-note" style={{ fontSize: 11, color: C.textMuted }}>Search note (optional)</label>
      <textarea
        id="ip-search-note"
        style={{ ...styles.textarea, minHeight: 60, marginTop: 4 }}
        value={draftNote}
        onChange={e => setDraftNote(e.target.value)}
        onFocus={() => { noteFocused.current = true; }}
        onBlur={e => {
          noteFocused.current = false;
          // Clicking the checkbox blurs this field FIRST, then fires the tick.
          // Committing here as well would make one act two PATCHes — and the
          // tick's own write already carries this exact draft, so the second one
          // would be pure duplication. Deferring is safe because the checkbox
          // flushes the draft on its own blur if the tick never arrives.
          //
          // jsdom does not move focus on `fireEvent.click`, so this path is
          // invisible to a click-only test; the test for it drives focus for
          // real. That is why the guard is on `relatedTarget` rather than on a
          // flag some synthetic event would have to remember to set.
          if ((e.relatedTarget as HTMLElement | null)?.id === ATTESTATION_ID) return;
          if (draftNote !== agreedNote.current) commitNote(draftNote);
          // Untouched draft, but the stored value moved while we held focus:
          // adopt it now rather than leaving a stale note on screen with no
          // further change to the prop to trigger the resync effect.
          else if (draftNote !== storedNote) { agreedNote.current = storedNote; setDraftNote(storedNote); }
        }}
        placeholder="Registries searched, terms used, and what was found — including a search that found nothing."
      />
      {/* #774 — the registry screenshots, licence PDFs and the like. */}
      <SectionEvidenceAttachments caseId={c.id} section="ip" />
    </div>
  );
}
